import { TranslationOutlined } from "@ant-design/icons";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Box, IconButton, Tooltip } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { SalonService } from "../../@type/salonService.types";
import { SalonAPI } from "../../api/Salon";
import { useMe } from "../../auth/MeContext";
import MuiTable from "../../components/common/MuiTable";
import ServiceEditModal, {
  ServiceCategoryOption,
} from "../../components/salon/ServiceEditModal";
import ServiceTranslationModal from "../../components/salon/ServiceTranslationModal";
import { useLanguages } from "../../hooks/useLanguages";
import { Column } from "../../utils/constants";

interface ServicesProps {
  outletId?: string;
}

const EMPTY_LANGUAGES: Languages[] = [];

const formatNumber = (value: number | null) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
};

const Services: React.FC<ServicesProps> = ({ outletId }) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useMe();
  const [{ data: languages }] = useLanguages();
  const [services, setServices] = useState<SalonService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<SalonService | null>(null);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchServices = useCallback(async () => {
    if (!outletId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await SalonAPI.getSalonServices(outletId);
      setServices(response.data.data || []);
    } catch (error) {
      console.error("Error fetching salon services:", error);
      setError(t("salon.services.load_failed"));
      toast.error(t("salon.services.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [outletId, t]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleOpenTranslations = (service: SalonService) => {
    setSelectedService(service);
    setTranslationModalOpen(true);
  };

  const handleOpenEdit = (service: SalonService) => {
    setSelectedService(service);
    setEditModalOpen(true);
  };

  const categoryOptions = useMemo<ServiceCategoryOption[]>(() => {
    const categoryMap = new Map<number, string>();
    services.forEach((service) => {
      if (service.category_id) {
        categoryMap.set(
          service.category_id,
          service.category_name || t("salon.services.category_fallback", { id: service.category_id })
        );
      }
    });

    return Array.from(categoryMap, ([id, name]) => ({ id, name }));
  }, [services, t]);

  const dataTable = useMemo(
    () =>
      services.map((service) => ({
        ...service,
        action: service.id,
      })),
    [services]
  );

  const header: Column[] = [
    {
      id: "name",
      label: `${t("common.table.name")}`,
      minWidth: 220,
      align: "center",
      format: (value: string, row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <span>{value || "-"}</span>
          <Tooltip title={t("salon.services.manage_translations")}>
            <IconButton
              size="small"
              aria-label={t("salon.services.manage_translations")}
              onClick={() => handleOpenTranslations(row as SalonService)}
            >
              <TranslationOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: "category_name",
      label: t("salon.services.category"),
      minWidth: 160,
      align: "center",
      format: (value: string | null) => value || "-",
    },
    {
      id: "price",
      label: t("salon.services.price"),
      minWidth: 120,
      align: "right",
      format: formatNumber,
    },
    {
      id: "discount_price",
      label: t("salon.services.discount_price"),
      minWidth: 150,
      align: "right",
      format: formatNumber,
    },
    {
      id: "duration",
      label: t("salon.services.duration"),
      minWidth: 120,
      align: "right",
      format: (value: number | null) =>
        value === null || value === undefined ? "-" : t("salon.services.minutes", { value }),
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 100,
      align: "center",
      format: (_value, row) =>
        isSuperAdmin ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Tooltip title={t("salon.services.edit")}>
              <IconButton
                size="small"
                aria-label={t("salon.services.edit")}
                onClick={() => handleOpenEdit(row as SalonService)}
              >
                <EditIcon color="success" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MuiTable header={header} data={dataTable} isLoading={loading}></MuiTable>

      <ServiceTranslationModal
        key={`translations-${selectedService?.id ?? "none"}`}
        open={translationModalOpen}
        outletId={outletId || ""}
        serviceId={selectedService?.id}
        serviceName={selectedService?.name}
        languages={languages || EMPTY_LANGUAGES}
        onCancel={() => setTranslationModalOpen(false)}
        onSuccess={fetchServices}
      />

      <ServiceEditModal
        key={`edit-${selectedService?.id ?? "none"}`}
        open={editModalOpen}
        outletId={outletId || ""}
        service={selectedService}
        categoryOptions={categoryOptions}
        onCancel={() => setEditModalOpen(false)}
        onSuccess={(updatedService, nameChanged) => {
          fetchServices();
          if (nameChanged) {
            // Existing translations are not regenerated automatically; prompt admin to review them.
            setSelectedService(updatedService);
            setTranslationModalOpen(true);
          }
        }}
        onManageTranslations={() => setTranslationModalOpen(true)}
      />
    </Box>
  );
};

export default Services;
